import type { ClassGroup as ApiClassGroup } from '@engage/api-v2/generated/../../api-v2-models/generated/ClassGroup';
import type { Playlist } from '@engage/api-v2/generated/../../api-v2-models/generated/Playlist';
import type { RideMetadataMappingsResponse } from '@engage/api-v2/generated/../../api-v2-models/generated/RideMetadataMappingsResponse';
import type { RideWithRelevance } from '@engage/api-v2/generated/../../api-v2-models/generated/RideWithRelevance';
import type { RideWithUserInfo } from '@engage/api-v2/generated/../../api-v2-models/generated/RideWithUserInfo';
import { unique } from '@peloton/helpers/unique';
import type { Locale } from '@peloton/internationalize/models/locale';
import { isDefined } from '@peloton/types';
import type {
  CollectionWithClasses,
  ClassGroup,
  CompleteClassGroupDetailsResponse,
} from '@engage/class-groups';
import type {
  ContentProvider,
  Metrics,
  ApiClassDetails,
  DifficultyLevel,
  RelevantSongInfo,
} from '@engage/classes';
import { mapRideToClass, isLibrary, isLive } from '@engage/classes';
import type {
  ProgramAssets,
  Instructor as InstructorFromGraphql,
  ProgramClass,
} from '@engage/graphql/types.generated';
import type { ClassPreview } from '@engage/library';
import { classToClassPreview } from '@engage/library';
import type {
  Equipment,
  Instructor,
  MetadataSelectorState,
  FitnessDiscipline,
} from '@engage/metadata';
import type { ProgramClassGroup } from './types';

export type Section = {
  data: RideWithUserInfo[];
  name?: string;
  description?: string;
  id: string;
};

export const toApiClassDetails = (ride: RideWithUserInfo): ApiClassDetails => ({
  captions: ride.captions!,
  pedalingStartOffset: ride.pedalingStartOffset!,
  classTypeIds: ride.rideTypeIds!,
  contentAvailability: ride.contentAvailability!,
  contentFormat: ride.contentFormat!,
  contentProvider: ride.contentProvider! as ContentProvider,
  description: ride.description!,
  difficultyLevel: (ride.difficultyLevel! as unknown) as DifficultyLevel,
  difficultyRatingAvg: ride.difficultyRatingAvg!,
  difficultyRatingCount: ride.difficultyRatingCount!,
  duration: ride.duration!,
  fitnessDiscipline: ride.fitnessDiscipline!,
  hasClosedCaptions: ride.hasClosedCaptions!,
  hasFreeMode: ride.hasFreeMode!,
  homePelotonId: ride.homePelotonId!,
  id: ride.id!,
  imageUrl: ride.imageUrl!,
  instructorId: ride.instructorId!,
  instructor: {
    name: ride?.instructor?.name!,
    imageUrl: ride?.instructor?.imageUrl!,
  },
  individualInstructorIds: ride.individualInstructorIds!,
  isArchived: ride.isArchived!,
  isExplicit: ride.isExplicit!,
  isLimitedRide: ride.isLimitedRide!,
  freeForLimitedTime: ride.freeForLimitedTime!,
  explicitRating: ride.explicitRating!,
  isFavorite: ride.isFavorite!,
  joinTokens: ride.joinTokens,
  language: ride.language!,
  liveStreamUrl: ride.liveStreamUrl!,
  originalAirTime: ride.originalAirTime!,
  originLocale: (ride.originLocale! as unknown) as Locale,
  overallRatingAvg: ride.overallRatingAvg!,
  overallRatingCount: ride.overallRatingCount!,
  pedalingEndOffset: ride.pedalingEndOffset!,
  scheduledStartTime: ride.scheduledStartTime!,
  studioPelotonId: ride.studioPelotonId!,
  title: ride.title!,
  totalFollowingWorkouts: ride.totalFollowingWorkouts!,
  totalUserWorkouts: ride.totalUserWorkouts!,
  totalWorkouts: ride.totalWorkouts!,
  equipmentIds: ride.equipmentIds!,
  isLiveInStudioOnly: ride.isLiveInStudioOnly!,
  metrics: ride.metrics as Metrics[],
});

/**
 * Extract relevant song info from ride (API response) or class preview
 * @param input ride or class preview
 * @returns RelevantSongInfo
 */
export const getRelevantSongInfo = (
  input: RideWithRelevance | ClassPreview,
): RelevantSongInfo | undefined => {
  if (isDefined(input.relevantSongTitle) && isDefined(input.numRelevantSongs)) {
    const { relevantSongTitle, numRelevantSongs, relevantAlbumImageUrl } = input;
    return { relevantSongTitle, numRelevantSongs, relevantAlbumImageUrl };
  }
  return undefined;
};

export const toClassPreview = (metadata: MetadataSelectorState['entities']) => (
  ride: RideWithRelevance & { playlist?: Playlist },
): ClassPreview | undefined => {
  const instructor = metadata.instructors[ride.instructorId!] ?? ({} as Instructor);
  const fitnessDiscipline =
    metadata.fitnessDisciplines[ride.fitnessDiscipline!] ?? ({} as FitnessDiscipline);
  const equipment = (ride.equipmentTags! as Equipment[]) ?? ([] as Equipment[]);
  const klass = mapRideToClass(toApiClassDetails(ride));
  const relevantSongInfo = getRelevantSongInfo(ride);

  if (
    isLibrary(klass) &&
    isDefined(instructor) &&
    isDefined(fitnessDiscipline) &&
    isDefined(equipment)
  ) {
    const isLiveClass = isLive(klass);
    const denormalizedClass = {
      ...klass,
      instructor,
      fitnessDiscipline,
      equipment,
      isLiveClass,
      ...relevantSongInfo,
      playlist: ride.playlist,
    };

    return classToClassPreview(denormalizedClass);
  }

  return undefined;
};

// This function is similiar to toClassPreview but instead of being called
// with redux sagas, its used with data returned from an SWR call
export const toClassPreviewsWithMetadata = (
  classes: RideWithRelevance[] = [],
  metadata: RideMetadataMappingsResponse,
): ClassPreview[] => {
  return classes
    .map(toClassPreviewWithMetadata(metadata))
    .filter(f => isDefined(f)) as ClassPreview[];
};
const toClassPreviewWithMetadata = (metadata: RideMetadataMappingsResponse) => (
  ride: RideWithRelevance & { playlist?: Playlist },
): ClassPreview | undefined => {
  const instructor =
    (metadata.instructors?.find(
      instructorItem => instructorItem.id === ride.instructorId,
    ) as Instructor) ?? ({} as Instructor);
  const fitnessDiscipline =
    (metadata.fitnessDisciplines?.find(
      fitnessDisciplineItem => fitnessDisciplineItem.id === ride.fitnessDiscipline,
    ) as FitnessDiscipline) ?? ({} as FitnessDiscipline);
  const equipment = (ride.equipmentTags! as Equipment[]) ?? ([] as Equipment[]);
  const klass = mapRideToClass(toApiClassDetails(ride));
  const relevantSongInfo = getRelevantSongInfo(ride);

  if (
    isLibrary(klass) &&
    isDefined(instructor) &&
    isDefined(fitnessDiscipline) &&
    isDefined(equipment)
  ) {
    const isLiveClass = isLive(klass);
    const denormalizedClass = {
      ...klass,
      instructor,
      fitnessDiscipline,
      equipment,
      isLiveClass,
      ...relevantSongInfo,
      playlist: ride.playlist,
    };
    return classToClassPreview(denormalizedClass);
  }

  return undefined;
};

export const toClassGroup = (metadata: MetadataSelectorState['entities']) => (
  apiClassGroup: ApiClassGroup,
): ClassGroup => {
  const instructors = isDefined(apiClassGroup.instructorIds)
    ? apiClassGroup.instructorIds.map(id => metadata.instructors[id])
    : [];

  return {
    instructors,
    id: apiClassGroup.id!,
    thumbnailImageUrl: apiClassGroup.thumbnailImageUrl!,
    bannerImageRight: apiClassGroup.heroImageUrl!,
    bannerImageCentered: apiClassGroup.heroImageCenteredUrl!,
    label: apiClassGroup.label!,
    length: apiClassGroup.length!,
    discipline: metadata.contentFocusLabels[apiClassGroup.contentFocusLabelId!].label,
    name: apiClassGroup.name!,
  };
};

export const toProgramsGroup = (metadata: MetadataSelectorState['entities']) => (
  program: any,
): ProgramClassGroup => {
  const instructors: Instructor[] = (program.instructors ?? []).map(
    (i: InstructorFromGraphql) => ({
      aboutImageUrl: i?.assets?.aboutImage?.location ?? '',
      imageUrl: i?.assets?.profileImage?.location ?? '',
      id: i.id,
      name: i.name,
      username: i.name,
      darkAboutImageUrl: metadata.instructors[i.id]?.darkAboutImageUrl,
      isVisible: metadata.instructors[i.id]?.isVisible,
      linkUrl: metadata.instructors[i.id]?.linkUrl,
    }),
  );

  const fitnessDisciplines: string[] = unique(
    program?.classes?.flatMap(
      (c: ProgramClass) => c?.pelotonClass.fitnessDiscipline.displayName ?? [],
    ) ?? [],
  );

  return {
    duration: program.programMetaData.duration,
    id: program.id,
    name: program.name,
    totalNumClasses: program.programMetaData.totalNumClasses,
    thumbnailImageUrl: program.assets.thumbnailImage.location,
    instructors: instructors,
    fitnessDisciplines,
    programMetaData: program.programMetaData,
    assets: program.assets as ProgramAssets,
  };
};

export const toCollectionWithClasses = (
  classGroupDetailsResponse: CompleteClassGroupDetailsResponse,
  metadata: MetadataSelectorState['entities'],
): CollectionWithClasses => {
  const instructors = isDefined(classGroupDetailsResponse.instructorIds)
    ? classGroupDetailsResponse.instructorIds.map(id => metadata.instructors[id])
    : [];

  const sections = classGroupDetailsResponse.sections! as Section[];

  const createClassPreviews = (section: Section) =>
    section.data
      .map(toClassPreview(metadata))
      .filter(f => isDefined(f)) as ClassPreview[];

  const mappedSections = sections.map(section => {
    const classes = createClassPreviews(section);
    return {
      id: section.id,
      classes,
      name: section.name,
      description: section.description,
    };
  });

  return {
    sections: mappedSections,
    instructors,
    id: classGroupDetailsResponse.id!,
    isLightThemedHero: classGroupDetailsResponse.heroImageDisplayOption! === 'light',
    thumbnailImageUrl: classGroupDetailsResponse.thumbnailImageUrl!,
    bannerImageRight: classGroupDetailsResponse.heroImageUrl!,
    bannerImageCentered: classGroupDetailsResponse.heroImageCenteredUrl!,
    label: classGroupDetailsResponse.label!,
    length: classGroupDetailsResponse.length!,
    description: classGroupDetailsResponse.description!,
    name: classGroupDetailsResponse.name!,
    discipline: metadata.contentFocusLabels[
      classGroupDetailsResponse.contentFocusLabelId!
    ].label!,
  };
};
